import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DollarSign, TrendingUp, FileSignature, Plus } from "lucide-react";

export const Route = createFileRoute("/billing/")({
  component: BillingDashboard,
});

function BillingDashboard() {
  const { data } = useQuery({
    queryKey: ["invoices"],
    queryFn: () => apiFetch("/api/billing/invoices", { schema: z.object({ items: z.array(z.any()) }) }),
  });

  const total = data?.items.reduce((s: number, i: any) => s + i.totalCents, 0) ?? 0;
  const paid = data?.items.filter((i: any) => i.status === "paid").reduce((s: number, i: any) => s + i.totalCents, 0) ?? 0;
  const pending = data?.items.filter((i: any) => i.status === "submitted" || i.status === "accepted").length ?? 0;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <DollarSign className="h-5 w-5" /> Facturation
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Vue d'ensemble des recettes</p>
        </div>
        <Button asChild>
          <Link to="/claims/new"><Plus className="h-4 w-4 mr-1" /> Nouvelle FSE</Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Total</div>
            <div className="mt-2 text-2xl font-bold">{(total / 100).toFixed(0)} MAD</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Encaissé</div>
            <div className="mt-2 text-2xl font-bold text-emerald-400">{(paid / 100).toFixed(0)} MAD</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">En attente</div>
            <div className="mt-2 text-2xl font-bold text-amber-400">{pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Taux recouvrement</div>
            <div className="mt-2 text-2xl font-bold">{total ? Math.round((paid / total) * 100) : 0}%</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dernières factures</CardTitle>
          <CardDescription>Voir toutes les <Link to="/claims" className="underline">feuilles de soins</Link></CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1">
            {data?.items.slice(0, 10).map((i: any) => (
              <li key={i.id} className="flex items-center justify-between text-sm py-1">
                <span>{i.invoiceNumber} · {i.patientName}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs">{(i.totalCents / 100).toFixed(2)} MAD</span>
                  <Badge variant="outline">{i.status}</Badge>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
